import React, { useEffect } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { getProductImageUrl } from 'src/utils/image';
import { ContentGenerationRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import moment from 'moment-timezone';
import { Select, MenuItem, FormControl, CircularProgress, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatMoney } from 'src/utils/stringFormatting';
import { ComparisonAnalysis } from '../api/apiTypes';
import { useSalesData } from '../hooks/useSalesData';
import { getQueryParamValue } from 'src/utils/app';
import { useGenerationVersion } from '../hooks/useGenerationVersion';
import { useContentGenReviewStore } from '../stores';
import { useStreamComparisonSummary } from '../hooks/useContentGenerate';

interface ContentGenSingleReviewHeaderProps {
  comparisonData: ComparisonAnalysis | null;
  currentContent?: any;
  generatedContent?: any;
  currentContentGenId?: string;
}

const StyledSelect = styled(Select)({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  },
  '& .MuiSelect-select': {
    padding: '4px 8px',
    color: colors.darkBlue,
    fontSize: '14px !important'
  },
  '& .MuiSvgIcon-root': {
    color: colors.darkBlue
  }
});

const ContentGenSingleReviewHeader: React.FC<ContentGenSingleReviewHeaderProps> = ({ 
  comparisonData, 
  currentContent, 
  generatedContent, 
  currentContentGenId 
}) => {
  const { updateCurrentContentGenItem, getCurrentContentGenItem } = useContentGenReviewStore();
  
  const {
    summary: streamingSummary,
    isStreaming,
    streamError,
    streamSummary
  } = useStreamComparisonSummary();

  const currentContentGenItem = getCurrentContentGenItem();

  const { salesData, isLoading: salesLoading } = useSalesData({
    retailerSku: currentContentGenItem?.retailerSku,
    stacklineSku: currentContentGenItem?.stacklineSku
  });

  const retailerId = getQueryParamValue('rid');

  const { data, isLoading: versionsLoading } = useGenerationVersion({
    retailerId: +retailerId,
    retailerSkus: [currentContentGenItem?.retailerSku]
  });

  // Trigger streaming summary when comparison data is available (must be before early return)
  useEffect(() => {
    if (comparisonData && !isStreaming && !streamingSummary && !streamError) {
      const { current_analysis, new_analysis } = comparisonData;
      
      if (current_analysis && new_analysis) {
        // Prepare content data for streaming
        const currentContentData = currentContent ? {
          title: currentContent.title,
          description: currentContent.description,
          bullets: currentContent.bullets
        } : undefined;

        const generatedContentData = generatedContent?.[currentContentGenId] ? {
          title: generatedContent[currentContentGenId].content.title,
          description: generatedContent[currentContentGenId].content.description,
          featureBullets: generatedContent[currentContentGenId].content.featureBullets
        } : undefined;

        streamSummary(current_analysis, new_analysis, currentContentData, generatedContentData);
      }
    }
  }, [comparisonData, isStreaming, streamingSummary, streamError, currentContent, generatedContent, currentContentGenId, streamSummary]);

  if (!currentContentGenItem) {
    return null;
  }

  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    const selectedVersion = event.target.value;
    const versionNumber = parseInt(selectedVersion.replace('v', ''), 10);
    const selectedVersionData = data?.find((v) => v.version === versionNumber);

    if (selectedVersionData && selectedVersionData.skuContentGenerationId) {
      updateCurrentContentGenItem({
        retailerSku: currentContentGenItem.retailerSku,
        product: currentContentGenItem.product,
        ...selectedVersionData
      });
    }
  };

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        backgroundColor: 'white',
        marginBottom: '24px'
      }}
    >
      {/* Headers Row */}
      <Flex gap={16} alignItems="center" justifyContent="space-between" sx={{ marginBottom: '8px' }}>
        <Flex width="437px">
          <Text variant="body2" sx={{ fontWeight: 500 }}>
            Product
          </Text>
        </Flex>
        <Flex gap={32} alignItems="center">
          <Flex width="100px">
            <Text variant="body2" sx={{ fontWeight: 500 }}>
              Sales (L52)
            </Text>
          </Flex>
          <Flex width="100px">
            <Text variant="body2" sx={{ fontWeight: 500 }}>
              Last Edited
            </Text>
          </Flex>
          <Flex width="100px">
            <Text variant="body2" sx={{ fontWeight: 500 }}>
              Profile
            </Text>
          </Flex>
          <Flex width="100px">
            <Text variant="body2" sx={{ fontWeight: 500 }}>
              Version
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {/* Content Row */}
      <Flex gap={16} alignItems="center" justifyContent="space-between">
        <Flex width="437px" gap={16} alignItems="center">
          {/* Product Image */}
          <img
            src={getProductImageUrl(currentContentGenItem.stacklineSku)}
            alt={currentContentGenItem.product?.title || 'Product image'}
            style={{
              width: '30px',
              height: '30px',
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />

          {/* Product Title */}
          <Text
            variant="body2"
            sx={{
              color: colors.darkBlue,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'pre-wrap',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}
          >
            {currentContentGenItem.product?.title || currentContentGenItem.retailerSku}
          </Text>
        </Flex>

        {/* Metrics Row */}
        <Flex gap={32} alignItems="center">
          {/* Sales Data */}
          <Flex width="100px" flexDirection="column" gap={2}>
            {salesLoading ? (
              <CircularProgress size={16} />
            ) : (
              <Text
                variant="body2"
                sx={{
                  color: colors.darkBlue
                }}
              >
                {formatMoney(salesData?.retailSales || currentContentGenItem.totalRetailSales || 0)}
              </Text>
            )}
          </Flex>

          {/* Last Edited */}
          <Flex width="100px" flexDirection="column" gap={2}>
            <Text
              variant="body2"
              sx={{
                color: colors.darkBlue
              }}
            >
              {moment(currentContentGenItem.updatedAt).format('MMM D, YYYY')}
            </Text>
          </Flex>

          {/* Profile */}
          <Flex width="100px" flexDirection="column" gap={2}>
            <Text
              variant="body2"
              sx={{
                color: colors.darkBlue
              }}
            >
              {currentContentGenItem.profileName || 'Default'}
            </Text>
          </Flex>

          {/* Version */}
          <Flex width="100px" flexDirection="column" gap={2}>
            <FormControl size="small">
              {versionsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <StyledSelect
                  value={`v${currentContentGenItem.version || 1}`}
                  onChange={handleVersionChange}
                  displayEmpty
                  disabled={!data || data.length <= 1}
                >
                  {data?.map((version) => (
                    <MenuItem key={version.version} value={`v${version.version}`}>
                      v{version.version}
                    </MenuItem>
                  ))}
                  {(!data || data.length === 0) && (
                    <MenuItem value={`v${currentContentGenItem.profileVersion || 1}`}>
                      v{currentContentGenItem.profileVersion || 1}
                    </MenuItem>
                  )}
                </StyledSelect>
              )}
            </FormControl>
          </Flex>
        </Flex>
      </Flex>

      {/* Content Overview Section - AI Streaming Summary - Only show when there's generated content */}
      {generatedContent && currentContentGenId && generatedContent[currentContentGenId] && (
        <Flex
          flexDirection="column"
          sx={{
            marginTop: '24px'
          }}
        >
          <Text
            variant="body2"
            sx={{
              color: colors.darkBlue,
              marginBottom: '8px',
              fontWeight: 500
            }}
          >
            Content Overview
          </Text>
          
          {streamError ? (
            <Text
              variant="body2"
              sx={{
                fontSize: '14px',
                color: '#d32f2f',
                lineHeight: 1.5
              }}
            >
              Failed to generate summary: {streamError.message}
            </Text>
          ) : (
            <Text
              variant="body2"
              sx={{
                fontSize: '14px',
                color: colors.darkBlue,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap'
              }}
            >
              {streamingSummary || (isStreaming ? 'Generating summary...' : (
                comparisonData?.comparison?.summary_text ||
                `The new content incorporates several elements that make it better: it has ${
                  comparisonData?.comparison?.summary?.keywords_added || 40
                } new keywords added, ${
                  comparisonData?.comparison?.summary?.keywords_removed
                    ? `removed ${comparisonData.comparison.summary.keywords_removed} keywords, `
                    : ''
                }and enhanced overall quality.`
              ))}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ContentGenSingleReviewHeader;
